import moment from 'moment';
import {
  ConversionCards,
  ConversionCardVm,
  ConversionComparison,
  ConversionMetricsRequest,
  ConversionRatio,
  ConversionWirePeriod,
} from '@partner/feature-modules/dashboard/domain/model/conversion-metrics.model';
import {
  DashboardDateRange,
  DashboardFilters,
  DashboardPeriod,
  DashboardScope,
  IsoDate,
} from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import { formatDateRange } from './dashboard-filters.helper';
import { toDashboardPeriodRequest } from './dashboard-request.helper';
import { DashboardSelection, sameSelection, toSelection } from './dashboard-vm.helper';
import { CARD_LABEL_EDGES, countLabel, higherIsBetterTier, percentLabel } from './pipeline-funnel.helper';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const SINGLE_DAY_FORMAT = 'MMM D, YYYY';

export const CONVERSION_CARD_ORDER: readonly (keyof ConversionCards)[] = [
  'submittedToApproved',
  'approvedToFunded',
  'startedToFunded',
];

export const CONVERSION_CARD_TITLES: Record<keyof ConversionCards, string> = {
  submittedToApproved: 'Submitted → Approved',
  approvedToFunded: 'Approved → Funded',
  startedToFunded: 'Started → Funded',
};

export const CONVERSION_WIRE_PERIODS: Record<DashboardPeriod, ConversionWirePeriod> = {
  [DashboardPeriod.MTD]: ConversionWirePeriod.MTD,
  [DashboardPeriod.QTD]: ConversionWirePeriod.QTD,
  [DashboardPeriod.YTD]: ConversionWirePeriod.YTD,
  [DashboardPeriod.CUSTOM]: ConversionWirePeriod.CUSTOM,
};

/** Only zero is an edge: a non-zero delta never prints as no change. */
export const DELTA_LABEL_EDGES: readonly number[] = [0];

/** The same dates compare differently per period, so this page's request is keyed on it too. */
export type ConversionSelection = DashboardSelection & { period: DashboardPeriod };

export function toConversionSelection(
  filters: DashboardFilters,
  range: DashboardDateRange | null,
): ConversionSelection | null {
  const selection = toSelection([filters, range]);
  return selection ? { ...selection, period: filters.period } : null;
}

export function sameConversionSelection(previous: ConversionSelection, current: ConversionSelection): boolean {
  return sameSelection(previous, current) && previous.period === current.period;
}

/** The shared half-open request plus `period`; null when the range is missing. */
export function toConversionMetricsRequest(
  range: DashboardDateRange | null,
  scope: DashboardScope,
  period: DashboardPeriod,
): ConversionMetricsRequest | null {
  const request = toDashboardPeriodRequest(range, scope);
  return request ? { ...request, period: CONVERSION_WIRE_PERIODS[period] } : null;
}

function parseIsoDate(value: IsoDate): moment.Moment | null {
  const parsed = moment(value, ISO_DATE_FORMAT, true);
  return parsed.isValid() ? parsed : null;
}

/** `vs Jun` / `vs Q2` / `vs 2025` / `vs May 5 – May 31, 2026`, from the server's window; null without one. */
export function comparisonLabel(comparison: ConversionComparison | null): string | null {
  const from = comparison ? parseIsoDate(comparison.from) : null;
  const to = comparison ? parseIsoDate(comparison.to) : null;
  if (!from || !to) {
    return null;
  }
  switch (comparison.period) {
    case ConversionWirePeriod.MTD:
      return `vs ${from.format('MMM')}`;
    case ConversionWirePeriod.QTD:
      return `vs Q${from.quarter()}`;
    case ConversionWirePeriod.YTD:
      return `vs ${from.year()}`;
    default: {
      const last = to.clone().subtract(1, 'day');
      if (last.isSame(from, 'day')) {
        return `vs ${from.format(SINGLE_DAY_FORMAT)}`;
      }
      const range = formatDateRange({ from: from.format(ISO_DATE_FORMAT), to: last.format(ISO_DATE_FORMAT) });
      return range ? `vs ${range}` : null;
    }
  }
}

/** `+4 pts vs Jun`, `-2 pts …`, `0 pts …`, `+1 pt …`; null when either part is missing. ASCII signs. */
export function deltaLabel(points: number | null, label: string | null): string | null {
  if (points === null || label === null) {
    return null;
  }
  const magnitude = percentLabel(Math.abs(points), DELTA_LABEL_EDGES).replace(/%$/, '');
  const sign = points > 0 ? '+' : points < 0 ? '-' : '';
  const unit = magnitude === '1' ? 'pt' : 'pts';
  return `${sign}${magnitude} ${unit} ${label}`;
}

/**
 * Repeats `toPipelineCardVm`'s body on purpose: that one is keyed on the pipeline cards, and choosing the tier
 * here means no caller can pair a conversion card with the wrong band.
 */
export function toConversionCardVm(
  card: keyof ConversionCards,
  ratio: ConversionRatio,
  label: string | null,
): ConversionCardVm {
  // A zero denominator reads as a zero rate on this page. The wire keeps null for the delta rule; Pipeline
  // Health keeps its dash.
  const percent = ratio.percent ?? 0;

  return {
    title: CONVERSION_CARD_TITLES[card],
    percentLabel: percentLabel(percent, CARD_LABEL_EDGES),
    countLabel: countLabel(ratio.numerator, ratio.denominator, null),
    progressPercent: percent,
    tier: higherIsBetterTier(percent),
    deltaLabel: deltaLabel(ratio.deltaPoints, label),
  };
}
